// src/services/productService.js
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, runTransaction, increment } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Stock is a count of physical garments, so it must be a whole number and can
 * never go below zero regardless of what was typed into a form.
 */
export const normalizeStock = (value) => {
  if (value === undefined || value === null || value === '') return 0;

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;

  return Math.max(0, Math.floor(numeric));
};

export const productService = {
  /**
   * Get all products from Firestore
   * @returns {Promise<Array>} Array of products with IDs
   */
  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      return querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get a single product by ID
   * @param {string} id - Product document ID
   * @returns {Promise<Object|null>} Product object or null if not found
   */
  async getById(id) {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  /**
   * Create a new product
   * @param {Object} productData - Product data to create
   * @returns {Promise<string>} Created product ID
   */
  async create(productData) {
    try {
      const stock = normalizeStock(productData.stock);
      
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        stock, // Ensure stock is always set
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (stock > 0) {
        await addDoc(collection(db, 'stockHistory'), {
          productId: docRef.id,
          productName: productData.name || 'Unknown Product',
          oldStock: 0,
          newStock: stock,
          change: stock,
          reason: 'Initial Stock',
          updatedBy: productData.createdBy || 'Admin',
          timestamp: new Date().toISOString()
        });
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  /**
   * Set a product's stock to an absolute value, logging the change.
   *
   * Transactional so `oldStock` in the audit trail is the value actually in
   * Firestore at write time, not whatever the admin form loaded earlier.
   * @returns {Promise<{changed: boolean, oldStock: number, newStock: number}>}
   */
  async setStock(id, newStockValue, reason = 'Manual Adjustment', updatedBy = 'Admin') {
    const newStock = normalizeStock(newStockValue);

    try {
      return await runTransaction(db, async (transaction) => {
        const productRef = doc(db, 'products', id);
        const productSnap = await transaction.get(productRef);

        if (!productSnap.exists()) {
          throw new Error('Product not found');
        }

        const productData = productSnap.data();
        const oldStock = normalizeStock(productData.stock);

        if (oldStock === newStock) {
          return { changed: false, oldStock, newStock };
        }

        transaction.update(productRef, {
          stock: newStock,
          updatedAt: new Date().toISOString()
        });

        transaction.set(doc(collection(db, 'stockHistory')), {
          productId: id,
          productName: productData.name || 'Unknown Product',
          oldStock,
          newStock,
          change: newStock - oldStock,
          reason,
          updatedBy,
          timestamp: new Date().toISOString()
        });

        return { changed: true, oldStock, newStock };
      });
    } catch (error) {
      console.error('Error setting product stock:', error);
      throw error;
    }
  },

  /**
   * Add (or remove) stock relative to whatever is currently stored.
   *
   * Uses an atomic increment so a concurrent sale is never overwritten - the
   * delta always applies on top of the live value.
   */
  async adjustStock(id, delta, reason = 'Stock Addition', updatedBy = 'Admin') {
    const change = Math.floor(Number(delta) || 0);

    if (change === 0) {
      return { changed: false, change: 0 };
    }

    try {
      const productRef = doc(db, 'products', id);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) {
        throw new Error('Product not found');
      }

      const productData = productSnap.data();

      await updateDoc(productRef, {
        stock: increment(change),
        updatedAt: new Date().toISOString()
      });

      // Read back so the history row reports the real resulting stock rather
      // than a value computed from a possibly stale read.
      const updatedSnap = await getDoc(productRef);
      const newStock = normalizeStock(updatedSnap.data()?.stock);

      await addDoc(collection(db, 'stockHistory'), {
        productId: id,
        productName: productData.name || 'Unknown Product',
        oldStock: newStock - change,
        newStock,
        change,
        reason,
        updatedBy,
        timestamp: new Date().toISOString()
      });

      return { changed: true, change, newStock };
    } catch (error) {
      console.error('Error adjusting product stock:', error);
      throw error;
    }
  },

  /**
   * Update an existing product
   * @param {string} id - Product document ID
   * @param {Object} productData - Updated product data
   * @returns {Promise<void>}
   */
  async update(id, productData) {
    try {
      // `stock` is deliberately excluded: every stock change has to go through
      // setStock/adjustStock so it is audited in stockHistory.
      const { stock, ...fields } = productData;

      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        ...fields,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  /**
   * Delete a product
   * @param {string} id - Product document ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = doc(db, 'products', id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  /**
   * Get featured products (marked as featured or random if none)
   * @param {number} count - Number of products to return
   * @returns {Promise<Array>} Array of featured products
   */
  async getFeatured(count = 4) {
    try {
      const products = await this.getAll();

      const isOutOfStock = (product) => {
        const hasStockData = product?.stock !== undefined && product?.stock !== null && product?.stock !== '';
        const numericStock = hasStockData ? Number(product.stock) : null;
        return (hasStockData && numericStock === 0) || product?.available === false;
      };

      // Featured section should show purchasable products first.
      const featuredInStock = products.filter(p => p.featured === true && !isOutOfStock(p));
      const nonFeaturedInStock = products.filter(p => p.featured !== true && !isOutOfStock(p));

      if (featuredInStock.length >= count) {
        return featuredInStock.slice(0, count);
      }

      if (featuredInStock.length > 0) {
        const remaining = count - featuredInStock.length;
        return [...featuredInStock, ...nonFeaturedInStock.slice(0, remaining)];
      }

      // No in-stock featured products; fall back to in-stock catalog items only.
      return nonFeaturedInStock.slice(0, count);
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  },

  /**
   * Get newest products sorted by creation date
   * @param {number} count - Number of products to return
   * @returns {Promise<Array>} Array of newest products
   */
  async getNewest(count = 4) {
    try {
      const products = await this.getAll();
      
      // Sort by createdAt descending (newest first)
      const sorted = [...products].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      
      return sorted.slice(0, count);
    } catch (error) {
      console.error('Error fetching newest products:', error);
      throw error;
    }
  },

  /**
   * Sync all products to ensure stock field exists and is a number
   * Sets stock to 0 for products without stock field
   * @returns {Promise<Object>} Summary of sync operation
   */
  async syncProductStock() {
    try {
      const products = await this.getAll();
      let updatedCount = 0;
      let alreadySyncedCount = 0;
      const results = [];

      for (const product of products) {
        // A missing, non-numeric or fractional value all normalise to a whole
        // number, so one comparison covers every case worth repairing.
        const normalized = normalizeStock(product.stock);

        if (product.stock === normalized) {
          alreadySyncedCount++;
          continue;
        }

        await updateDoc(doc(db, 'products', product.id), {
          stock: normalized,
          updatedAt: new Date().toISOString()
        });

        await addDoc(collection(db, 'stockHistory'), {
          productId: product.id,
          productName: product.name || 'Unknown Product',
          oldStock: product.stock ?? null,
          newStock: normalized,
          change: normalized - normalizeStock(product.stock),
          reason: 'Stock Field Sync',
          updatedBy: 'Admin',
          timestamp: new Date().toISOString()
        });

        results.push({
          id: product.id,
          name: product.name,
          oldStock: product.stock,
          newStock: normalized,
          type: 'converted'
        });
        updatedCount++;
      }

      return {
        success: true,
        totalProducts: products.length,
        updatedCount,
        alreadySyncedCount,
        results
      };
    } catch (error) {
      console.error('Error syncing product stock:', error);
      throw error;
    }
  }
};
